import React, { useEffect, useRef } from 'react';
import { Wifi } from 'lucide-react';
import gsap from 'gsap';

const TRUSTED_LOGOS = [
  "https://i.imgur.com/qQ1N9F1.png",
  "https://i.imgur.com/AVvBXEm.png",
  "https://i.imgur.com/DNpiS1U.png",
];

const Partners: React.FC = () => {
  const MARQUEE_SETS = Array(12).fill(TRUSTED_LOGOS);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sectionRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            const tl = gsap.timeline();
            tl.fromTo('.partner-header', { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8 })
              .fromTo('.partner-circle', { scale: 0.8, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.8, ease: "back.out(1.7)" }, "-=0.4")
              .fromTo('.partner-marquee', { opacity: 0 }, { opacity: 1, duration: 1 }, "-=0.2");
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(sectionRef.current);
      return () => observer.disconnect();
    }
  }, []);

  return (
    <section ref={sectionRef} className="pb-16 pt-20 overflow-hidden w-full border-t border-slate-200 dark:border-slate-800/40">
      <div className="flex flex-col items-center justify-center text-center">
        
        {/* Header with gradient lines */}
        <div className="partner-header w-full flex items-center justify-center gap-4 mb-8 px-6 opacity-0">
            <div className="h-px w-12 md:w-24 bg-gradient-to-r from-transparent to-slate-300 dark:to-slate-700"></div>
            <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
                <Wifi size={14} className="text-primary" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] whitespace-nowrap font-bold">
                    Official Digital Partner
                </span>
            </div>
             <div className="h-px w-12 md:w-24 bg-gradient-to-l from-transparent to-slate-300 dark:to-slate-700"></div>
        </div>

        {/* Circular Partner Logo Display */}
        <div className="partner-circle relative mb-6 px-6 opacity-0">
            {/* Subtle glow effect behind the circle */}
            <div className="absolute inset-0 rounded-full bg-primary/20 blur-2xl opacity-30"></div>

            {/* Circular Frame */}
            <div className="relative w-24 h-24 md:w-32 md:h-32 rounded-full bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center justify-center overflow-hidden shadow-2xl shadow-black/10">
                {/* Logo */}
                <img 
                    src="https://i.imgur.com/SpikROP.png" 
                    alt="Audio Mark" 
                    className="w-full h-full object-cover"
                />
            </div>
        </div>

        {/* Trusted By Section - Marquee */}
        <div className="partner-marquee w-full opacity-0">
            <div className="flex items-center justify-center mb-6 px-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 font-bold">
                    Trusted By
                </span>
            </div>

            <div className="relative w-full overflow-hidden">
                {/* Gradient Masks for fade effect */}
                <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-32 z-10 bg-gradient-to-r from-background-light dark:from-background-dark to-transparent pointer-events-none"></div>
                <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-32 z-10 bg-gradient-to-l from-background-light dark:from-background-dark to-transparent pointer-events-none"></div>
                
                {/* Marquee Track */}
                <div className="flex items-center w-max animate-marquee hover:[animation-play-state:paused]">
                    {MARQUEE_SETS.map((set, setIndex) => (
                        <div key={setIndex} className="flex items-center gap-8 md:gap-16 px-4 md:px-8 shrink-0">
                            {set.map((logo: string, i: number) => {
                                const isLarge = i < 2;
                                return (
                                    <div 
                                        key={`${setIndex}-${i}`} 
                                        className={`relative flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer ${
                                            isLarge ? 'h-12 sm:h-20 md:h-32' : 'h-8 sm:h-12 md:h-20'
                                        }`}
                                    >
                                        <img 
                                            src={logo} 
                                            alt={`Partner ${i + 1}`} 
                                            className="h-full w-auto object-contain max-w-none opacity-80 hover:opacity-100 transition-opacity"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>

      </div>
    </section>
  );
};

export default Partners;